if (!customElements.get('related-products-v2-carousel')) {
  class RelatedProductsV2Carousel extends HTMLElement {
    connectedCallback() {
      this.bindDrag();
      this.bindWheelRedirect();
    }

    bindWheelRedirect() {
      // Browsers treat an overflow-x:auto element as a scroll container for
      // wheel events, which traps page scroll when the cursor is over the
      // carousel. Detect primarily-vertical wheels and redirect them to the
      // page so the user can keep scrolling the page as normal.
      this.addEventListener(
        'wheel',
        (e) => {
          const absX = Math.abs(e.deltaX);
          const absY = Math.abs(e.deltaY);
          if (absY > absX) {
            e.preventDefault();
            window.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
          }
          // Horizontal intent (absX >= absY) -> let native overflow-x scroll.
        },
        { passive: false }
      );
    }

    bindDrag() {
      let isDown = false;
      let startX = 0;
      let startScrollLeft = 0;
      let pointerId = null;
      const DRAG_THRESHOLD = 4;
      let dragged = false;

      const onDown = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        if (e.target.closest('a, button, input, select, textarea, [role="button"]')) return;
        isDown = true;
        dragged = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        startScrollLeft = this.scrollLeft;
        try { this.setPointerCapture(pointerId); } catch (_) {}
      };

      const onMove = (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (!dragged && Math.abs(dx) > DRAG_THRESHOLD) {
          dragged = true;
          this.classList.add('is-dragging');
        }
        if (dragged) {
          this.scrollLeft = startScrollLeft - dx;
          e.preventDefault();
        }
      };

      const onUp = (e) => {
        if (!isDown) return;
        try { this.releasePointerCapture(pointerId); } catch (_) {}
        isDown = false;
        if (dragged) {
          this.classList.remove('is-dragging');
          // Suppress click that immediately follows a drag.
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          this.addEventListener('click', swallow, { capture: true, once: true });
        }
        dragged = false;
        pointerId = null;
      };

      this.addEventListener('pointerdown', onDown);
      this.addEventListener('pointermove', onMove);
      this.addEventListener('pointerup', onUp);
      this.addEventListener('pointercancel', onUp);
    }
  }
  customElements.define('related-products-v2-carousel', RelatedProductsV2Carousel);
}

if (!customElements.get('related-products-v2-scrollbar')) {
  class RelatedProductsV2Scrollbar extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.related-products-v2__bar');
      this.thumb = this.querySelector('.related-products-v2__thumb');
      this.prevBtn = this.querySelector('.related-products-v2__arrow--prev');
      this.nextBtn = this.querySelector('.related-products-v2__arrow--next');
      if (!this.bar || !this.thumb || !this.prevBtn || !this.nextBtn) return;

      const sectionId = this.dataset.sectionId;
      const sectionEl = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
      this.scope = sectionEl || document;

      this.onScroll = () => this.update();
      this.onResize = () => this.update();

      this.prevBtn.addEventListener('click', () => this.scroll(-0.85));
      this.nextBtn.addEventListener('click', () => this.scroll(0.85));
      window.addEventListener('resize', this.onResize);
      window.addEventListener('load', () => this.rebind());
      this.bindBarDrag();

      requestAnimationFrame(() => this.rebind());
    }

    disconnectedCallback() {
      if (this.currentScrollable) {
        this.currentScrollable.removeEventListener('scroll', this.onScroll);
      }
      window.removeEventListener('resize', this.onResize);
      this.resizeObserver?.disconnect();
    }

    rebind() {
      if (this.currentScrollable) {
        this.currentScrollable.removeEventListener('scroll', this.onScroll);
      }
      this.resizeObserver?.disconnect();

      this.currentScrollable = this.scope.querySelector('related-products-v2-carousel');
      if (!this.currentScrollable) {
        this.classList.add('tw-hidden');
        return;
      }

      this.currentScrollable.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.currentScrollable);
      requestAnimationFrame(() => this.update());
    }

    update() {
      const el = this.currentScrollable;
      if (!el) {
        this.classList.add('tw-hidden');
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 1) {
        this.classList.add('tw-hidden');
        return;
      }
      this.classList.remove('tw-hidden');

      const visibleRatio = el.clientWidth / el.scrollWidth;
      const thumbWidth = Math.max(0.12, visibleRatio);
      const progress = el.scrollLeft / maxScroll;
      const thumbLeft = progress * (1 - thumbWidth);

      this.thumb.style.width = `${(thumbWidth * 100).toFixed(2)}%`;
      this.thumb.style.left = `${(thumbLeft * 100).toFixed(2)}%`;
      this.prevBtn.disabled = el.scrollLeft <= 1;
      this.nextBtn.disabled = el.scrollLeft >= maxScroll - 1;
    }

    scroll(ratio) {
      if (!this.currentScrollable) return;
      this.currentScrollable.scrollBy({
        left: this.currentScrollable.clientWidth * ratio,
        behavior: 'smooth',
      });
    }

    bindBarDrag() {
      let dragging = false;
      let pointerId = null;

      const getProgress = (clientX) => {
        const rect = this.bar.getBoundingClientRect();
        return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      };

      const applyProgress = (progress) => {
        const el = this.currentScrollable;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) return;
        const visibleRatio = el.clientWidth / el.scrollWidth;
        const thumbWidth = Math.max(0.12, visibleRatio);
        const thumbLeft = Math.max(0, Math.min(1 - thumbWidth, progress - thumbWidth / 2));
        const scrollProgress = (1 - thumbWidth) > 0 ? thumbLeft / (1 - thumbWidth) : 0;
        el.scrollLeft = scrollProgress * maxScroll;
      };

      this.bar.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        dragging = true;
        pointerId = e.pointerId;
        try { this.bar.setPointerCapture(pointerId); } catch (_) {}
        this.thumb.style.transitionDuration = '0ms';
        this.bar.style.cursor = 'grabbing';
        this.bar.classList.add('is-dragging');
        applyProgress(getProgress(e.clientX));
      });

      this.bar.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        e.preventDefault();
        applyProgress(getProgress(e.clientX));
      });

      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        try { this.bar.releasePointerCapture(pointerId); } catch (_) {}
        pointerId = null;
        this.thumb.style.transitionDuration = '';
        this.bar.style.cursor = 'pointer';
        this.bar.classList.remove('is-dragging');
      };

      this.bar.addEventListener('pointerup', onUp);
      this.bar.addEventListener('pointercancel', onUp);

      this.bar.style.cursor = 'pointer';
      this.bar.style.touchAction = 'none';
    }
  }
  customElements.define('related-products-v2-scrollbar', RelatedProductsV2Scrollbar);
}
